<script setup lang="ts">
  import Table from '@/components/table/Table.vue'
  import TableFilter from '@/components/table/TableFilter.vue'
  import Tabs from '@/components/Tabs.vue'
  import TopBar from '@/components/TopBar.vue'
  import { useOpenAlert } from '@/composables/use-open-alert'
  import { useTabQuery } from '@/composables/use-tab-query'
  import { useAlertStore } from '@/store/alert.store'
  import { AlertStatus, type Alert } from '@/types/nest/alert'
  import AlertDetailSheet from '@/views/alerts/components/AlertDetailSheet.vue'
  import {
    DateRangePicker,
    UiButton,
    type Column,
    type Nullish,
    type Tab,
    useToastStore,
  } from '@logistiek-zonder-papier/lzp-library'
  import {
    endOfDay,
    format,
    formatDistanceToNowStrict,
    isAfter,
    isBefore,
    isToday,
    isYesterday,
  } from 'date-fns'
  import { nl } from 'date-fns/locale'
  import { useTranslation } from 'i18next-vue'
  import { computed, onMounted, ref } from 'vue'

  type AlertRow = Alert & { message: string }

  const tabs: Tab[] = [
    { key: 'inbox', label: 'Inbox' },
    { key: 'archived', label: 'Gearchiveerd' },
  ]

  const columns: Column[] = [
    { key: 'message', label: 'Melding', searchable: true, width: '40rem' },
    { key: 'createdAt', label: 'Datum' },
    { key: 'actions', label: 'Acties', sticky: 'right' },
  ]

  const alertStore = useAlertStore()
  const toastStore = useToastStore()
  const { i18next } = useTranslation()
  const { openAlert } = useOpenAlert()

  const activeTab = useTabQuery(tabs, 'inbox')
  const fromDate = ref<Date | Nullish>()
  const toDate = ref<Date | Nullish>()
  const selectedAlertId = ref<string | null>(null)
  const showDetail = ref<boolean>(false)

  const isArchiveTab = computed(() => activeTab.value === 'archived')

  const rows = computed<AlertRow[]>(() =>
    alertStore.alerts
      .filter(alert => (alert.status === AlertStatus.ARCHIVED) === isArchiveTab.value)
      .filter(alert => isInDateRange(new Date(alert.createdAt)))
      .map(alert => ({ ...alert, message: `${alert.title} ${alert.description ?? ''}` }))
  )

  const selectedAlert = computed(
    () => alertStore.alerts.find(alert => alert.id === selectedAlertId.value) ?? null
  )

  const dateFilterLabel = computed(() => {
    if (fromDate.value && toDate.value) {
      return `${formatDate(fromDate.value)} – ${formatDate(toDate.value)}`
    }
    if (fromDate.value) return `Vanaf ${formatDate(fromDate.value)}`
    if (toDate.value) return `Tot ${formatDate(toDate.value)}`
    return 'Alle datums'
  })

  onMounted(async () => {
    await alertStore.fetchAlerts()
  })

  function formatDate(date: Date) {
    return date.toLocaleDateString(i18next.language, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  function formatDateTime(date: string) {
    const createdAt = new Date(date)
    if (isToday(createdAt)) {
      return formatDistanceToNowStrict(createdAt, { addSuffix: true, locale: nl })
    }
    if (isYesterday(createdAt)) return `Gisteren, ${format(createdAt, 'HH:mm')}`
    return format(createdAt, 'd MMM yyyy, HH:mm', { locale: nl })
  }

  function isInDateRange(date: Date) {
    if (fromDate.value && isBefore(date, fromDate.value)) return false
    if (toDate.value && isAfter(date, endOfDay(toDate.value))) return false
    return true
  }

  function clearDates() {
    fromDate.value = null
    toDate.value = null
  }

  async function updateStatus(alert: Alert, status: AlertStatus, message: string) {
    await alertStore.updateAlertStatus(alert.id, status)
    toastStore.success(message)
  }

  async function markAllAsRead() {
    await alertStore.markAllAlertsAsRead()
    toastStore.success('Alle meldingen gemarkeerd als gelezen')
  }

  async function openDetail(alert: AlertRow) {
    selectedAlertId.value = alert.id
    showDetail.value = true

    if (alert.status === AlertStatus.UNREAD) {
      await alertStore.updateAlertStatus(alert.id, AlertStatus.READ)
    }
  }
</script>

<template>
  <TopBar />

  <AlertDetailSheet v-model:show="showDetail" :alert="selectedAlert" />

  <div class="alerts-page">
    <Tabs v-model="activeTab" :tabs="tabs" class="alerts-tabs">
      <template v-for="tab in tabs" :key="tab.key" #[tab.key]>
        <div class="toolbar">
          <TableFilter
            :label="dateFilterLabel"
            :clearable="fromDate != null || toDate != null"
            @clear="clearDates"
          >
            <DateRangePicker
              :fromOptions="{ label: 'Van' }"
              :toOptions="{ label: 'Tot' }"
              v-model:from="fromDate"
              v-model:to="toDate"
            />
          </TableFilter>

          <UiButton
            v-if="rows.some(row => row.status === AlertStatus.UNREAD)"
            variant="link"
            size="small"
            icon="check"
            icon-position="left"
            label="Alles gelezen"
            @click="markAllAsRead"
          />
        </div>

        <Table :rows="rows" :columns="columns" row-key="id" @row-click="openDetail">
          <template #cell-message="{ row }">
            <div class="message" :class="{ 'is-unread': row.status === AlertStatus.UNREAD }">
              <span class="unread-dot" />

              <div class="message-content">
                <span class="message-title">{{ row.title }}</span>

                <span v-if="row.description" class="message-description">
                  {{ row.description }}
                </span>
              </div>
            </div>
          </template>

          <template #cell-createdAt="{ row }">
            <span class="date">{{ formatDateTime(row.createdAt) }}</span>
          </template>

          <template #cell-actions="{ row }">
            <div class="actions" @click.stop>
              <UiButton
                v-if="row.status === AlertStatus.UNREAD"
                class="action-status"
                variant="link"
                icon="check"
                title="Markeer als gelezen"
                aria-label="Markeer als gelezen"
                @click="updateStatus(row, AlertStatus.READ, 'Melding gemarkeerd als gelezen')"
              />

              <UiButton
                v-if="row.status === AlertStatus.READ"
                class="action-status"
                variant="link"
                icon="eyeSlash"
                title="Markeer als ongelezen"
                aria-label="Markeer als ongelezen"
                @click="updateStatus(row, AlertStatus.UNREAD, 'Melding gemarkeerd als ongelezen')"
              />

              <UiButton
                v-if="row.status === AlertStatus.ARCHIVED"
                class="action-archive"
                variant="link"
                icon="refresh"
                title="Terugzetten"
                aria-label="Terugzetten"
                @click="updateStatus(row, AlertStatus.READ, 'Melding teruggezet')"
              />

              <UiButton
                v-else
                class="action-archive"
                variant="link"
                icon="archive"
                title="Archiveren"
                aria-label="Archiveren"
                @click="updateStatus(row, AlertStatus.ARCHIVED, 'Melding gearchiveerd')"
              />

              <UiButton
                v-if="row.url"
                class="action-open"
                variant="link"
                icon="arrowRight"
                title="Bekijk"
                aria-label="Bekijk"
                @click="openAlert(row)"
              />
            </div>
          </template>

          <template #empty>
            {{ isArchiveTab ? 'Geen gearchiveerde meldingen' : 'Geen meldingen' }}
          </template>
        </Table>
      </template>
    </Tabs>
  </div>
</template>

<style lang="css" scoped>
  .alerts-page {
    display: flex;
    flex-direction: column;
    min-height: calc(100dvh - var(--top-bar-height));
  }

  .alerts-tabs {
    flex: 1;
  }

  .alerts-tabs :deep(.tab-panel) {
    flex: 1;
  }

  .toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .message {
    display: flex;
    align-items: start;
    gap: var(--spacing-xs);
    white-space: normal;
    cursor: pointer;
  }

  .unread-dot {
    flex-shrink: 0;
    width: 0.5rem;
    height: 0.5rem;
    margin-top: 0.375rem;
    border-radius: 50%;
  }

  .is-unread .unread-dot {
    background-color: var(--colours-brand-colour-1);
  }

  .message-content {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;
  }

  .is-unread .message-title {
    font-weight: 600;
  }

  .message-description {
    display: -webkit-box;
    overflow: hidden;
    color: var(--colours-grey-4);
    font-size: 0.8125rem;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .date {
    color: var(--colours-grey-4);
  }

  .actions {
    display: grid;
    grid-template-columns: repeat(3, 3rem);
    gap: var(--spacing-xs);
  }

  .actions :deep(svg) {
    width: 20px;
    height: 20px;
  }

  .action-status {
    grid-column: 1;
  }

  .action-archive {
    grid-column: 2;
  }

  .action-open {
    grid-column: 3;
  }
</style>
